import { SelectOption } from '@focaldata/cin-ui-components'
import {
  FieldworkAudience,
  FieldworkAudienceType,
  MemberSettingCode
} from '../../data/model/audience'
import { truncateText } from '../../utils/questionnaireUtils'
import {
  getAudienceMemberQuestion,
  getAudienceMemberQuestionId,
  getSurveyQuestionLabels,
  inScreenerOrder,
  isHiddenFromSurveyQuestion
} from './audienceOrder'
import { getEffectiveCriterionOptions, getIsEnabledInSettings } from './utils'

export interface ConditionRow {
  questionId: string
  responseOptionIds: string[]
  negated: boolean
}

export interface SourceQuestion {
  questionId: string
  label: string
  isTargeting: boolean
  responseOptions: { id: string; value: string }[]
  /** Never asked means never answered, so a rule over it could not fire. */
  isAsked: boolean
  /** Screens out before a rule over a later question can be reached. */
  screensOutOnItsOwn: boolean
}

// A never-asked question is excluded rather than offered and then rejected on save.
export const getSourceQuestions = (
  members: FieldworkAudience[] | undefined
): SourceQuestion[] => {
  const ordered = inScreenerOrder(members)
  // Over the full list, before filtering, or the numbers shift.
  const labels = getSurveyQuestionLabels(ordered)

  return ordered
    .map((member, index) => {
      const question = getAudienceMemberQuestion(member)
      const qualifyingOn = getIsEnabledInSettings(
        member.memberItem.settingValues,
        MemberSettingCode.Qualifying
      )
      const isTargeting = member.memberType === FieldworkAudienceType.Standard

      const options = isTargeting
        ? getEffectiveCriterionOptions(member.memberItem).map((option) => ({
            id: option.criterionOption.responseOption.responseOptionId,
            value: option.criterionOption.responseOption.value,
            qualification: option.qualification
          }))
        : member.memberItem.criterionOptions.map((option) => ({
            id: option.responseOptionLk,
            value: option.responseOption.value,
            qualification: option.qualification
          }))

      return {
        questionId: getAudienceMemberQuestionId(member),
        label: `${labels[index] ?? ''} ${truncateText(
          40,
          question.text
        )}`.trim(),
        isTargeting,
        responseOptions: options.map(({ id, value }) => ({ id, value })),
        isAsked: !isHiddenFromSurveyQuestion(member),
        screensOutOnItsOwn:
          qualifyingOn && options.some((option) => !option.qualification)
      }
    })
    .filter(
      (question) => question.isAsked && question.responseOptions.length > 0
    )
}

export const toSelectOptions = (questions: SourceQuestion[]): SelectOption[] =>
  questions.map((question, index) => ({
    id: `${index}`,
    name: question.label,
    value: question.questionId
  }))

// Grouping stays per clause: hoisting it out merges rows on the same question.
export const clauseToRows = <
  T extends {
    proposition: { questionId: string; responseOptionId: string }
    negated: boolean
  }
>(
  condition: T[][]
): ConditionRow[] =>
  condition.flatMap((clause) => {
    const byQuestion = new Map<string, ConditionRow>()

    clause.forEach(({ proposition, negated }) => {
      const existing = byQuestion.get(proposition.questionId)
      if (existing) {
        existing.responseOptionIds.push(proposition.responseOptionId)
        return
      }
      byQuestion.set(proposition.questionId, {
        questionId: proposition.questionId,
        responseOptionIds: [proposition.responseOptionId],
        negated
      })
    })

    return [...byQuestion.values()]
  })

export const isRowComplete = (row: ConditionRow): boolean =>
  !!row.questionId && row.responseOptionIds.length > 0

export const describeRow = (
  row: ConditionRow,
  questions: SourceQuestion[]
): string => {
  const question = questions.find((q) => q.questionId === row.questionId)
  const label = question?.label ?? 'a deleted question'
  const values = row.responseOptionIds
    .map(
      (id) =>
        question?.responseOptions.find((option) => option.id === id)?.value ??
        '?'
    )
    .join(', ')
  return `${label} ${row.negated ? 'is not one of' : 'is one of'} ${values}`
}
